import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import {
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  Divider,
  FormControl,
} from '@material-ui/core'
import { MultiSelect } from 'react-multi-select-component'

import { Dialog, UploadButton, snackbar } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { PRIZE } from 'consts'
import { EditorInput } from 'features/Main/components'
import { StringUtils } from 'utils'
import { PrizeCategory } from 'consts/prize'
import { PartnerIntegration } from 'features/Main/components/PartnerIntegration'
import { Tag } from 'features/Main/Tags/types'

import { CreatePrizePayload, CreatePrizeResponse, Prize } from '../../../../Prizes/types'
import { createPrize } from '../../../../Prizes/api'
import { useStyles } from './styles'
import { REWARD_TYPE_BY_MEMBERSHIP } from '../constants'

interface ValidationData {
  toteatEnabled: boolean
  category?: PrizeCategory
}

interface TagOption {
  label: string
  value: number
}

const rules = {
  title: [
    {
      validate: (title: string) => StringUtils.extractTextFromHTML(title).length <= 70,
      message: 'No puede superar los 70 caracteres',
    },
  ],
  shortTitle: [
    {
      validate: (shortTitle: string) => shortTitle.length <= 22,
      message: 'No puede superar los 22 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string) => (value && value.length <= 100) || !value.length,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  icon: [],
  expirationDays: [
    {
      validate: (value: string) =>
        ((value && Number(value) >= 1) || !value) && Number.isInteger(Number(value)),
      message: 'El vencimiento debe ser un número entero mayor a 0',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID Beneficio Toteat es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
}

interface CreateMembershipPrizeDialogProps {
  handleClose: () => void
  onDone: (prize: Prize) => void
  membershipId: number
  tags: Tag[]
}

const CreatePrizeDialog: React.FC<CreateMembershipPrizeDialogProps> = ({
  handleClose,
  onDone,
  membershipId,
  tags,
}) => {
  const classes = useStyles()

  const [toteatBenefitId, setToteatBenefitId] = useState<string>('')
  const [currentSku, setCurrentSku] = useState<string>('')
  const [skusList, setSkusList] = useState<string[]>([])
  const [storesEnabled, setStoresEnabled] = useState(true)
  const [tucanEnabled, setTucanEnabled] = useState(false)
  const [toteatEnabled, setToteatEnabled] = useState(false)
  const [category, setCategory] = useState<PrizeCategory | undefined>()
  const [title, setTitle] = useState('')
  const [shortTitle, setShortTitle] = useState('')
  const [icon, setIcon] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState('')
  const [additionalText, setAdditionalText] = useState('')
  const [rewardType, setrewardType] = useState('')
  const [expirationDays, setExpirationDays] = useState<string>('30')
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)

  const [tagsSelected, setTagsSelected] = useState<TagOption[]>([])

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))

  const handleSetTags = async (e: TagOption[]) => {
    setTagsSelected(e)
  }

  const [createMembershipPrizeApi, isLoading] = useApiCall<CreatePrizePayload, CreatePrizeResponse>(
    createPrize,
  )

  const handleIconChange = (file: File) => {
    setIconPreview(URL.createObjectURL(file))
    setIcon(file)
  }

  const handlerewardTypeSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    setrewardType(event.target.value as string)
  }

  const getRequiredFields = () => {
    const requiredFields = [
      'title',
      'shortTitle',
      'additionalText',
      'icon',
      ...(rewardType === PRIZE.REWARD_TYPE.AUTOMATIC ? ['expirationDays'] : []),
    ]

    if (toteatEnabled) {
      if (category === PrizeCategory.General) {
        requiredFields.push('skusList')
      } else if (category === PrizeCategory.Checkout) {
        requiredFields.push('toteatBenefitId')
      }
    }

    return requiredFields
  }

  const fields = {
    title,
    shortTitle,
    additionalText,
    icon,
    expirationDays,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    category,
  }
  const { isAnyFieldEmpty, hasErrors, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )
  const isSubmitDisabled = isAnyFieldEmpty || hasErrors || hasPartnerErrors

  const onCreateMembershipPrize = async () => {
    if (!icon) return
    const prize = {
      membershipId,
      title,
      shortTitle,
      icon,
      additionalText,
      rewardType,
      expirationDays,
      category,
      storesEnabled,
      tucanEnabled,
      toteatEnabled,
      skus: skusList,
      toteatBenefitId,
      tagsSelected,
    }
    try {
      const { prize: newPrize } = await createMembershipPrizeApi(prize)
      onDone(newPrize)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo crear el premio. Intente de nuevo.')
    }
  }

  const handlePartnerValidationChange = (hasErrorsPartner: boolean) => {
    setHasPartnerErrors(hasErrorsPartner)
  }

  return (
    <Dialog
      title="Nuevo premio"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onCreateMembershipPrize}
      style={{ width: '100%' }}
    >
      <Grid container spacing={3}>
        <PartnerIntegration
          storesEnabled={storesEnabled}
          setStoresEnabled={setStoresEnabled}
          tucanEnabled={tucanEnabled}
          setTucanEnabled={setTucanEnabled}
          toteatEnabled={toteatEnabled}
          setToteatEnabled={setToteatEnabled}
          category={category}
          setCategory={setCategory}
          currentSku={currentSku}
          setCurrentSku={setCurrentSku}
          skusList={skusList}
          setSkusList={setSkusList}
          toteatBenefitId={toteatBenefitId}
          setToteatBenefitId={setToteatBenefitId}
          errors={errors}
          isLoading={isLoading}
          onValidationChange={handlePartnerValidationChange}
        />

        <Grid item xs={12}>
          <Divider variant="middle" />
        </Grid>

        <Grid item xs={12}>
          <InputLabel id="demo-simple-select-label">Tipo de premio</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={rewardType}
            onChange={handlerewardTypeSelect}
            fullWidth
          >
            {REWARD_TYPE_BY_MEMBERSHIP.map(value => (
              <MenuItem value={value.id} key={value.id}>
                {value.description}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12}>
          <EditorInput
            value={title}
            label="Título premio"
            readOnly={isLoading}
            required
            onChange={setTitle}
            maxLength={70}
            errorText={rules.title[0].message}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Descripcion premio"
            value={shortTitle}
            fullWidth
            onChange={e => setShortTitle(e.target.value)}
            disabled={isLoading}
            required
            error={errors.shortTitle.hasError}
            helperText={errors.shortTitle.message}
            inputProps={{ maxLength: 22 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Texto adicional premio"
            value={additionalText}
            fullWidth
            onChange={e => setAdditionalText(e.target.value)}
            disabled={isLoading}
            required
            error={errors.additionalText.hasError}
            helperText={errors.additionalText.message}
          />
        </Grid>

        {rewardType === PRIZE.REWARD_TYPE.AUTOMATIC && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                value={expirationDays}
                type="number"
                fullWidth
                onChange={e => setExpirationDays(e.target.value)}
                disabled={isLoading}
                label="Vencimiento"
                required
                InputProps={{
                  inputProps: { min: 1 },
                  endAdornment: <InputAdornment position="start">días</InputAdornment>,
                }}
                error={errors.expirationDays.hasError}
                helperText={errors.expirationDays.message}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <InputLabel shrink className={classes.multiselectTitle}>
            Tag
          </InputLabel>
          <FormControl className={classes.fullWidth}>
            <MultiSelect
              options={tagsForSelect}
              value={tagsSelected}
              onChange={handleSetTags}
              ClearIcon
              labelledBy="Seleccionar Tags"
              hasSelectAll={false}
              className={classes.outlinedMultiSelect}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.iconUploadContainer}>
            <UploadButton
              id="manualReward-icon-upload-button"
              accept=".jpg, .jpeg, .png"
              label="Subir ícono premio"
              onChange={handleIconChange}
            />
          </div>
        </Grid>
        {iconPreview && (
          <>
            <Grid item xs={12}>
              <div className={classes.iconUploadPreviewContainer}>
                <img src={iconPreview} alt="" className={classes.iconPreview} />
              </div>
            </Grid>
          </>
        )}
      </Grid>
    </Dialog>
  )
}

export { CreatePrizeDialog }
